import { Shape, ShapeType } from '@/services/shape';
import { canUse } from '@/utils/can-use.utils';
import { encodeHTMLEntities } from '../utils/name.utils';
import { buildAstState } from '..';
import { getAppWidgetAst } from './app-widget';
import { getConditionAst, getLayoutAstWithCondition } from './condition';
import { getImageAst } from './image';
import { getImageArrayAst } from './image-array';
import { getImageArrayWidgetDate } from './image-array/imageArrayWidgetDate';
import { getImageArrayWidgetTime } from './image-array/imageArrayWidgetTime';
import { getJsFileAst } from './js-file';
import { getApkFileAst } from './apk-file';
import { getProgressBarAst } from './progressBar';
import { getSlotAst } from './slot';
import { getSpriteAst } from './sprite';
import { getTextAst } from './text';
import { getWidgetAst } from './widget';
import { bindWidgetAnimation, getAnimationAst } from './animation';
import { getRiveAst } from './rive';
import { ElectronApi } from '@/services/electron.api';
import { DataSourceFormat } from '@/resources/enum';

/**
 * 根据shapes生成表盘协议的layout ast
 * @param resourceAst
 * @param shapes
 * @returns
 */
export const shapesToLayoutAst = (
  resourceAst: Ast,
  layers: StoreLayerItem[],
  parent: StoreLayerItem | undefined
) => {
  const res: Ast[] = [];
  layers.forEach((layer) => {
    const shape = Shape.getShape(layer.id, layer);
    if (!shape) return;

    const { type, widgetImageArray } = shape;
    let layoutAst: Ast | Ast[] | undefined;

    const { source,sourceFormat } = shape.attrs;
    switch (type) {
      case ShapeType.Image:
        layoutAst = getImageAst(resourceAst, shape, res, parent);
        break;
      case ShapeType.ImageArray: {
        // 图组：普通图组直接用 getImageArrayAst；
        // 时间 / 日期组合图组（widgetImageArray）需要把全局缩放一起传入内部子元素
        const theme = buildAstState.data?.theme;
        const themeScale = theme?.scale ?? 1;
        const shapeScale = shape.attrs.scale ?? 1;
        const effectiveScaleForWidget = themeScale * shapeScale;
        if (widgetImageArray) {
          layoutAst =
            widgetImageArray == 'time'
              ? ((sourceFormat == DataSourceFormat['Format_8:00:00'] ||
                  sourceFormat == DataSourceFormat['Format_08:00:00'])?getImageArrayWidgetTime(
                  resourceAst,
                  shape,
                  source + 'Hour',
                  source + 'Minute',
                  parent,
                  source + 'Second',
                  effectiveScaleForWidget
                ):getImageArrayWidgetTime(
                  resourceAst,
                  shape,
                  source + 'Hour',
                  source + 'Minute',
                  parent,
                  undefined,
                  effectiveScaleForWidget
                ))
              : getImageArrayWidgetDate(
                  resourceAst,
                  shape,
                  parent,
                  effectiveScaleForWidget
                );
        } else {
          layoutAst = getImageArrayAst(resourceAst, shape);
        }
        break;
      }
      case ShapeType.ProgressBar:
        layoutAst = getProgressBarAst(resourceAst, shape);
        break;
      case ShapeType.Text:
        if (canUse('shape-text').valid) {
          layoutAst = getTextAst(resourceAst, shape);
        }
        break;
      case ShapeType.Sprite:
        layoutAst = getSpriteAst(resourceAst, shape);
        break;
      case ShapeType.Widget:
        layoutAst = getWidgetAst(resourceAst, shape, layer, parent);
        break;
      case ShapeType.Slot:
        layoutAst = getSlotAst(resourceAst, shape, layer);
        break;
      case ShapeType.JsFile:
        layoutAst = getJsFileAst(resourceAst, shape);
        break;
      case ShapeType.AppWidget:
        layoutAst = getAppWidgetAst(resourceAst, shape);
        break;
      case ShapeType.Rive:
        layoutAst = getRiveAst(resourceAst, shape);
        break;
      case ShapeType.ApkFile:
        layoutAst = getApkFileAst(resourceAst, shape);
        break;
    }
    if (layoutAst) {
      const _layoutAst = [layoutAst].flat();
      // 全局缩放只针对图片和图组：仅对 Image/ImageArray 应用 themeScale × 元素 scale
      const theme = buildAstState.data?.theme;
      const themeScale = theme?.scale ?? 1;
      const shapeScale = shape.attrs.scale ?? 1;
      const onlyImageOrArray =
        (type === ShapeType.Image || type === ShapeType.ImageArray) &&
        !shape.attrs?._maskFlag;
      const effectiveScale = onlyImageOrArray
        ? themeScale * shapeScale
        : (shape.attrs.scale ?? 1);

      _layoutAst.forEach((item) => {
        item.attrsMap._id = item.attrsMap._id ? item.attrsMap._id : shape.id;
        item.attrsMap._shapeId = item.attrsMap._shapeId
          ? item.attrsMap._shapeId
          : shape.id;
        item.attrsMap._name = encodeHTMLEntities(shape.attrs.name ?? '');
        item.attrsMap._scale = String(effectiveScale);
        item.attrsMap._alpha = String(shape.attrs.alpha ?? '1');
        if (!shape.attrs._visibility) item.attrsMap._hidden = 'true';
      });
      res.push(..._layoutAst);
    }
  });
  return res;
};

export const animationToLayoutAst = (
  resourceAst: Ast,
  ifttts: IFTTT[],
  shapeLayoutAst: Ast[]
) => {
  // animations
  // 1. 通过animation里面的trigger生成条件出来
  // 2. 根据条件 + animation绑定的图层id去创建出图层对应的Slot出来
  // 3. 最后修改layout跟slot的映射关系
  ifttts
    // 过滤不可使用ifttt的图层
    .filter((ifttt) => {
      return Shape.getShape(ifttt.shapeId)?.canUseIFTTT ? true : false;
    })
    // 过滤未设置触发条件的动效
    .filter((ifttt) => {
      return ifttt.condition.children.length > 0 ? true : false;
    })
    // 过滤未有动画过程的
    .filter((ifttt) => {
      return ifttt.animations.length ? true : false;
    })
    .forEach((ifttt) => {
      const { shapeId } = ifttt;
      // 先生成条件控制部分
      const conditionAst = getConditionAst(resourceAst, ifttt.condition);

      // 如果是非show类型的话，需要创建animation，并为节点绑定动画
      const othersAnimations = ifttt.animations?.filter(
        (item) => item.type !== 'show'
      );
      if (othersAnimations.length > 0) {
        const animationAst = getAnimationAst(
          resourceAst,
          othersAnimations,
          ifttt
        );
        if (animationAst) {
          bindWidgetAnimation(
            resourceAst,
            [shapeId],
            animationAst,
            conditionAst,
            shapeLayoutAst
          );
        }
      }

      // 如果是show类型的话，不需要创建出animation, 仅绑定condition条件即可
      const showAnimations = ifttt.animations?.filter(
        (item) => item.type === 'show'
      );
      if (showAnimations.length > 0) {
        getLayoutAstWithCondition(
          resourceAst,
          conditionAst,
          shapeLayoutAst.filter((item) => item.attrsMap._shapeId === shapeId),
          shapeLayoutAst,
          Shape.getShape(shapeId)
        );
      }
    });
  return shapeLayoutAst;
};
